import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  BookOpen,
  Loader2,
  Facebook,
  Instagram,
  Twitter,
  MessageCircle,
  Link2,
  Check,
  ArrowRight,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { EBOOK, openEbook, submitEbookLead, track } from "@/lib/ebook";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FAQ from "@/components/FAQ";
import SEO from "@/components/SEO";

const EBOOK_FAQS = [
  {
    q: "Is the guide really free?",
    a: "Yes — completely free. We share it because every great canvas starts with the right photograph, and the right photograph is the difference between pretty and timeless.",
  },
  {
    q: "Will I be added to spam or text marketing?",
    a: "No. Your email is used to deliver the guide and occasional Crowned Canvas updates — unsubscribe anytime. If you share your phone number, we will only text you if you explicitly check the SMS consent box.",
  },
  {
    q: "What happens after I sign up?",
    a: "You'll be taken straight to the Crowned Perfect Photo Guide, delivered through our Payhip library. Your details stay with Crowned Canvas Creations.",
  },
];

const INSIDE = [
  "What separates a phone photo from a canvas photograph",
  "Lighting & resolution standards our studio looks for",
  "Composition, wardrobe & expression guidance",
  "How to brief our studio for a result you'll love",
];

export default function EbookPage() {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [smsConsent, setSmsConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const scrollDepthRef = useRef(0);

  useEffect(() => {
    track("ebook_page_view", "ebook-page");
    const onScroll = () => {
      const doc = document.documentElement;
      const pct = ((window.scrollY || doc.scrollTop) / ((doc.scrollHeight || 1) - window.innerHeight)) * 100;
      [25, 50, 75, 100].forEach((bucket) => {
        if (pct >= bucket && scrollDepthRef.current < bucket) {
          scrollDepthRef.current = bucket;
          track("ebook_scroll_depth", `${bucket}%`);
        }
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !email.trim()) return;
    // Phone is optional; SMS consent only applies when a number is provided.
    const consent = phone.trim() ? smsConsent : false;
    setBusy(true);
    try {
      await submitEbookLead({
        email: email.trim(),
        name: firstName.trim(),
        phone: phone.trim(),
        sms_consent: consent,
        source: "ebook-page",
      });
      toast.success(`You're in, ${firstName.trim()}. Opening your guide now.`);
      openEbook("ebook-page");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't sign up. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleShare = (platform) => {
    track("share_click", platform);
    const u = encodeURIComponent(EBOOK.url);
    const t = encodeURIComponent(`${EBOOK.title} — a free guide from Crowned Canvas Creations.`);
    let href = "";
    if (platform === "facebook") href = `https://www.facebook.com/sharer/sharer.php?u=${u}&quote=${t}`;
    else if (platform === "twitter") href = `https://twitter.com/intent/tweet?url=${u}&text=${t}`;
    else if (platform === "whatsapp") href = `https://api.whatsapp.com/send?text=${t}%20${u}`;
    if (href) window.open(href, "_blank", "noopener,noreferrer,width=600,height=600");
  };

  const handleInstagram = async () => {
    track("share_click", "instagram");
    if (navigator.share) {
      try { await navigator.share({ title: EBOOK.title, text: EBOOK.title, url: EBOOK.url }); return; } catch (_) {}
    }
    try { await navigator.clipboard.writeText(`${EBOOK.title} ${EBOOK.url}`); toast.success("Link copied — paste it into Instagram."); } catch (_) {}
  };

  const handleCopy = async () => {
    track("share_click", "copy-link");
    try { await navigator.clipboard.writeText(EBOOK.url); toast.success("Link copied."); } catch (_) {}
  };

  return (
    <div className="relative">
      <SEO
        title="Free Perfect Photo Guide | Crowned Canvas Creations"
        description="The free Crowned Perfect Photo Guide shows you what makes a photograph stronger for custom artwork before you submit it. From Crowned Canvas Creations, Madison, Wisconsin."
        path="/ebook"
      />
      <Header />
      <main className="pt-24 md:pt-32">
        {/* HERO */}
        <section className="relative">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_20%_30%,rgba(130,73,55,0.3),transparent_60%)]" aria-hidden />
          <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-12 md:py-20 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-5 order-2 lg:order-1">
              <div className="relative group max-w-[420px] mx-auto lg:mx-0">
                <div className="absolute inset-0 sm:-inset-3 bg-[#D4AF37]/10 blur-3xl group-hover:bg-[#D4AF37]/20 transition-colors" aria-hidden />
                <div className="relative aspect-[3/4] border border-[#D4AF37]/40 overflow-hidden">
                  <img src={EBOOK.cover} alt="The Crowned Perfect Photo Guide — cover artwork" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/30 to-transparent" />
                  <div className="absolute top-5 right-5 bg-[#D4AF37] text-black overline px-3 py-1.5">Free</div>
                  <div className="absolute inset-x-6 bottom-6">
                    <p className="overline text-[#D4AF37]">Crowned Canvas Creations</p>
                    <p className="font-serif-display text-white text-2xl md:text-3xl leading-tight mt-3">
                      {EBOOK.title}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7 order-1 lg:order-2">
              <p className="overline">Free Crowned Guide</p>
              <h1 className="font-serif-display text-4xl md:text-5xl lg:text-[60px] leading-[1.02] mt-5 tracking-tight">
                Before we create the masterpiece,<br />
                let&apos;s find the <span className="italic text-[#D4AF37]">right photo.</span>
              </h1>
              <p className="text-white/70 mt-6 max-w-xl font-light leading-relaxed text-base md:text-lg">
                Your photograph is the foundation of your finished artwork. The
                Crowned Perfect Photo Guide helps you understand what makes a
                photograph stronger for custom artwork before you submit it.
              </p>

              <ul className="mt-7 grid sm:grid-cols-2 gap-3 max-w-xl">
                {INSIDE.map((p) => (
                  <li key={p} className="flex items-start gap-2.5 text-sm text-white/75">
                    <Check className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>

              {/* LEAD CAPTURE — first name + email required, phone optional w/ SMS consent */}
              <form onSubmit={handleSubmit} data-testid="ebook-page-form" className="mt-9 max-w-xl space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="ebook-first-name" className="overline text-white/60">First name *</Label>
                    <Input
                      id="ebook-first-name"
                      required
                      data-testid="ebook-page-first-name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="Your first name"
                      className="bg-[#111111] border-white/15 h-14 rounded-none text-white focus-visible:ring-1 focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ebook-email" className="overline text-white/60">Email *</Label>
                    <Input
                      id="ebook-email"
                      required
                      type="email"
                      data-testid="ebook-page-email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@email.com"
                      className="bg-[#111111] border-white/15 h-14 rounded-none text-white focus-visible:ring-1 focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37]"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ebook-phone" className="overline text-white/60">Phone (optional)</Label>
                  <Input
                    id="ebook-phone"
                    type="tel"
                    data-testid="ebook-page-phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+1 555 000 0000"
                    className="bg-[#111111] border-white/15 h-14 rounded-none text-white focus-visible:ring-1 focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37]"
                  />
                </div>
                {phone.trim() && (
                  <div className="flex items-start gap-3 border border-white/10 bg-[#111111] p-4">
                    <Checkbox
                      id="ebook-sms-consent"
                      data-testid="ebook-page-sms-consent"
                      checked={smsConsent}
                      onCheckedChange={(v) => setSmsConsent(!!v)}
                      className="mt-0.5 border-white/30 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:text-black"
                    />
                    <Label htmlFor="ebook-sms-consent" className="text-xs text-white/65 font-light leading-relaxed cursor-pointer">
                      Optional: I agree to receive text messages from Crowned Canvas Creations
                      about my guide and Crowned experiences. You are NOT enrolled in SMS
                      marketing unless you check this box. Msg &amp; data rates may apply.
                      Reply STOP anytime.
                    </Label>
                  </div>
                )}
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="ebook-page-submit"
                  className="group inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-black px-7 h-14 text-xs uppercase tracking-[0.22em] font-semibold hover:bg-[#e6c245] transition-all disabled:opacity-50 whitespace-nowrap"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Get The Free Photo Guide<ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></>}
                </button>
                <p className="text-white/40 text-[11px] leading-relaxed">
                  After sign-up you&apos;ll be directed to the guide, delivered through
                  the Crowned Canvas Creations library on Payhip.
                </p>
              </form>
            </div>
          </div>
        </section>

        {/* Share */}
        <section className="py-16 md:py-20 border-t border-white/5">
          <div className="max-w-[1100px] mx-auto px-6 md:px-12">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
              <div>
                <p className="overline">Pass The Crown</p>
                <h2 className="font-serif-display text-3xl md:text-4xl mt-3 leading-tight">
                  Share the guide.
                </h2>
              </div>
              <p className="text-white/55 max-w-md font-light">
                Know someone with a story worth framing? Send them the guide.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <ShareBtn testid="ebook-share-facebook" onClick={() => handleShare("facebook")} Icon={Facebook} label="Facebook" />
              <ShareBtn testid="ebook-share-instagram" onClick={handleInstagram} Icon={Instagram} label="Instagram" />
              <ShareBtn testid="ebook-share-twitter" onClick={() => handleShare("twitter")} Icon={Twitter} label="X / Twitter" />
              <ShareBtn testid="ebook-share-whatsapp" onClick={() => handleShare("whatsapp")} Icon={MessageCircle} label="WhatsApp" />
              <ShareBtn testid="ebook-share-copy" onClick={handleCopy} Icon={Link2} label="Copy Link" />
            </div>
          </div>
        </section>

        {/* FAQ */}
        <FAQ items={EBOOK_FAQS} title="About the guide." testidPrefix="ebook-faq" />

        {/* Next step CTA */}
        <section className="py-20 md:py-28 border-t border-white/5 bg-black/40">
          <div className="max-w-[900px] mx-auto px-6 text-center">
            <p className="overline">After The Read</p>
            <h2 className="font-serif-display text-3xl md:text-5xl mt-4 leading-tight">
              Ready to crown <span className="italic text-[#D4AF37]">your moment?</span>
            </h2>
            <div className="flex flex-wrap gap-3 justify-center mt-9">
              <Link to="/collection/sovereign" data-testid="ebook-to-sovereign" className="inline-flex items-center gap-3 bg-[#D4AF37] text-black px-7 py-4 text-xs uppercase tracking-[0.22em] font-semibold hover:bg-[#e6c245] transition-all">
                Explore Sovereign Canvas<ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/#experiences" className="inline-flex items-center gap-3 border border-white/20 text-white/85 px-7 py-4 text-xs uppercase tracking-[0.22em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all">
                Compare Experiences
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function ShareBtn({ Icon, label, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="inline-flex items-center gap-2 border border-white/15 px-4 py-2.5 text-xs uppercase tracking-[0.18em] text-white/80 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </button>
  );
}
